import classNames from "classnames";
import BaseButton from "components/baseButton/baseButton";
import BaseInput from "components/baseInput/baseInput";
import OpacityLoader from "components/baseLoader/opacityLoader";
import BaseSelect, { Option } from "components/baseSelect/baseSelect";
import BaseToggleSwitch from "components/baseToggleSwitch/baseToggleSwitch";
import { FormikErrors, useFormik } from "formik";
import { useEffect, useState } from "react";
import { Form, Modal, ModalHeader } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { confirmModal, declineModal } from "redux/reducers/modalReducer";
import { executeAxiosRequestWithRefresh } from "redux/services";
import { useAppDispatch } from "redux/store";
import { ModalTypeEnum, ShopperInfoRequirementType, SupportedLanguageEnum } from "utils/enums";
import { getLanguages } from "utils/helperFunctions";
import { StringResources } from "utils/language/languageResource";
import * as Yup from "yup";
import "./createTokenizeLinkModal.scss";
import {
    CreateTokenizeLinkModalProps,
    ICreateTokenizeByLinkRequestForm,
    IEcomResponseData,
    ITokenizeLinkShopperInfo,
    ITokenizeLinkThemeResponseData,
    shopperInfoDataInitialValues,
} from "./createTokenizeLinkModel";
import { baseTokenizeByLinkRoute } from "pages/tokenizeByLink/api/tokenizeByLinkApi";
import BaseUtcDatePicker from "components/baseUtcDatePicker/baseUtcDatePicker";
import { DateTime } from "luxon";

const languages = getLanguages();
const today = DateTime.now().startOf("day");
const tomorrow = today.plus({ days: 1 });

const CreateTokenizeLinkModal = (props: CreateTokenizeLinkModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [ecomTerminalsOptions, setEcomTerminalsOptions] = useState<Array<Option>>([]);
    const [ecomTerminals, setEcomTerminals] = useState<Array<IEcomResponseData>>([]);
    const [ecomThemeOptions, setEcomThemeOptions] = useState<Array<Option>>([]);

    useEffect(() => {
        const fetchEcomTerminals = async () => {
            try {
                const response = await executeAxiosRequestWithRefresh({
                    url: `${baseTokenizeByLinkRoute}/get-tokenize-by-link-request-data`,
                    method: "GET",
                });

                if (response && response.data) {
                    const ecomTerminalsData = response.data.tokenizeByLinkResponseData;
                    const ecomThemesData = response.data.tokenizeLinkThemeResponseData;

                    await setEcomTerminals(ecomTerminalsData);

                    await setEcomTerminalsOptions(ecomTerminalsOptions);

                    const ecomThemeOptions: Array<Option> = ecomThemesData.map((x: ITokenizeLinkThemeResponseData) => {
                        return {
                            label: `${x.themeId} - ${x.themeName} ${
                                x.themeDescription ? "- " + x.themeDescription : ""
                            }`,
                            value: x.themeId,
                            selected: false,
                        };
                    });

                    await setEcomThemeOptions(ecomThemeOptions);

                    await setIsLoading(false);
                }
            } catch (error) {
                toast.error(`${t(StringResources.modal.createTokenizeLink.fetchPaymentPageError)}`);
            }
        };

        fetchEcomTerminals();
    }, []);

    // We added this to remove form notch element on each DOM change inside document.body because it creates bug inside input element
    // We will refactor this once we change Input elements
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const formNotches = document.querySelectorAll(".form-notch");
            formNotches.forEach(notch => notch.remove());
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, []);

    const getInitialValues = () => {
        const defaultOrderedShopperInfoData = shopperInfoDataInitialValues.map(initialInfo => {
            return {
                ...initialInfo,
                label: t(initialInfo.label),
            };
        });

        if (!props.data.isUpdate && !props.data.isCopy) {
            const data: ICreateTokenizeByLinkRequestForm = {
                ecomSolutionId: undefined,
                contractId: undefined,
                recurringProcessingModel: undefined,
                themeId: undefined,
                language: SupportedLanguageEnum.Icelandic,
                hasExpirationDate: undefined,
                tokenizeByLinkDescription: undefined,
                linkReuse: undefined,
                dateTo: undefined,
                shopperInfoData: defaultOrderedShopperInfoData,
            };

            return data;
        }

        const existingData = props.data.existingData!;
        const recurringProcessingModelMap = {
            CardOnFile: 1,
            Subscription: 2,
            UnscheduledCardOnFile: 3,
        };

        // Maintain order by mapping over initial values and merging with existing data
        const orderedShopperInfoData = defaultOrderedShopperInfoData.map(defaultInfo => {
            const existingInfo = existingData.shopperInfoData.find(x => x.field === defaultInfo.field);
            if (existingInfo) {
                return {
                    ...defaultInfo,
                    shopperInfoRequirementType: existingInfo.shopperInfoRequirementType,
                    value: existingInfo.value,
                };
            }

            return defaultInfo;
        });

        const data: ICreateTokenizeByLinkRequestForm = {
            ecomSolutionId: existingData.ecomSolutionId,
            contractId: existingData.contractId,
            recurringProcessingModel:
                recurringProcessingModelMap[
                    existingData.recurringProcessingModel as keyof typeof recurringProcessingModelMap
                ],
            themeId: existingData.themeId,
            language: existingData.languageType,
            hasExpirationDate: !!existingData.validTo,
            tokenizeByLinkDescription: existingData.tokenizeByLinkDescription || "",
            linkReuse: !existingData.exactSalesCount,
            dateTo: existingData.validTo ? new Date(existingData.validTo) : undefined,
            shopperInfoData: orderedShopperInfoData,
        };

        return data;
    };

    const formValidation = useFormik<ICreateTokenizeByLinkRequestForm>({
        enableReinitialize: true,
        validateOnChange: true,
        validateOnMount: false,
        validateOnBlur: true,
        initialValues: getInitialValues(),
        validationSchema: Yup.object({
            ecomSolutionId: Yup.number().required(
                t(StringResources.modal.createTokenizeLink.ecomTerminalToUseRequired)
            ),
            recurringProcessingModel: Yup.number().required(
                t(StringResources.modal.createTokenizeLink.reasonForTokenizationRequired)
            ),
            language: Yup.number().required(t(StringResources.modal.createTokenizeLink.languageRequired)),
            hasExpirationDate: Yup.boolean().required(
                t(StringResources.modal.createTokenizeLink.hasExpirationDateRequired)
            ),
            dateTo: Yup.date().when("hasExpirationDate", {
                is: true,
                then: Yup.date()
                    .min(today.toJSDate())
                    .required(t(StringResources.modal.createTokenizeLink.validToRequired)),
                otherwise: Yup.date().nullable(),
            }),
            linkReuse: Yup.boolean().required(t(StringResources.modal.createTokenizeLink.linkReUseRequired)),
            tokenizeByLinkDescription: Yup.string().nullable(),
            shopperInfoData: Yup.array().of(
                Yup.object().shape({
                    shopperInfoRequirementType: Yup.number().required(
                        t(StringResources.modal.createTokenizeLink.shopperInfoRequirementTypeRequired)
                    ),
                    field: Yup.number().required(t(StringResources.modal.createTokenizeLink.shopperInfoFieldRequired)),
                    value: Yup.string().when("shopperInfoRequirementType", {
                        is: ShopperInfoRequirementType.Prefilled,
                        then: Yup.string().required(
                            t(StringResources.modal.createTokenizeLink.shopperInfoValueRequired)
                        ),
                        otherwise: Yup.string().nullable(),
                    }),
                    label: Yup.string().nullable(),
                })
            ),
        }),
        onSubmit: async (value: ICreateTokenizeByLinkRequestForm) => {
            let shopperInfoDataForRequest = formValidation.values.shopperInfoData
                .filter(x => x.shopperInfoRequirementType != ShopperInfoRequirementType.Hidden)
                .map(({ label, ...item }) => item);

            try {
                const requestData = {
                    contractId: value.contractId,
                    ecomSolutionId: value.ecomSolutionId,
                    recurringProcessingModel: value.recurringProcessingModel,
                    themeId: value.themeId,
                    tokenizeByLinkDescription: value.tokenizeByLinkDescription,
                    linkReuse: value.linkReuse,
                    languageType: value.language,
                    validTo: value.hasExpirationDate ? value.dateTo : null,
                    shopperInfos: shopperInfoDataForRequest,
                };

                if (props.data.existingData && !props.data.isCopy) {
                    const updateRequestData = {
                        ...requestData,
                        tokenizeLinkId: props.data.tokenizeByLinkId,
                    };

                    await executeAxiosRequestWithRefresh({
                        url: `${baseTokenizeByLinkRoute}/update-tokenize-link`,
                        method: "POST",
                        data: updateRequestData,
                    });
                } else {
                    await executeAxiosRequestWithRefresh({
                        url: `${baseTokenizeByLinkRoute}/create-tokenize-link`,
                        method: "POST",
                        data: requestData,
                    });
                }

                dispatch(confirmModal({ modalType: ModalTypeEnum.CreateTokenizeLink }));
                toast.success(
                    props.data.isUpdate
                        ? `${t(StringResources.modal.createTokenizeLink.successUpdateMessage)}`
                        : `${t(StringResources.modal.createTokenizeLink.successMessage)}`
                );
            } catch (error: any) {
                toast.error(`${t(StringResources.modal.createTokenizeLink.errorMessage)}`);
            }
        },
    });

    const reasonsForTokenization = [
        {
            label: `${t(StringResources.modal.createTokenizeLink.cardOnFile)}`,
            value: 1,
            description: `${t(StringResources.modal.createTokenizeLink.cardOnFileTooltip)}`,
        },
        {
            label: `${t(StringResources.modal.createTokenizeLink.subscription)}`,
            value: 2,
            description: `${t(StringResources.modal.createTokenizeLink.subscriptionTooltip)}`,
        },
        {
            label: `${t(StringResources.modal.createTokenizeLink.unscheduledCardOnFile)}`,
            value: 3,
            description: `${t(StringResources.modal.createTokenizeLink.unscheduledCardOnFileTooltip)}`,
        },
    ];

    const onPaymentPageChange = async (selectedItem: Option | undefined) => {
        if (selectedItem && selectedItem.value !== 0) {
            const contractId = +selectedItem.value.toString().split("-")[0];
            const ecomSolutionId = +selectedItem.value.toString().split("-")[1];

            await formValidation.setFieldValue("ecomSolutionId", ecomSolutionId);
            await formValidation.setFieldValue("contractId", contractId);
        } else {
            await formValidation.setFieldValue("ecomSolutionId", undefined);
            await formValidation.setFieldValue("contractId", undefined);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        formValidation.handleSubmit();
    };

    const onCancelClick = () => {
        dispatch(declineModal({ modalType: ModalTypeEnum.CreateTokenizeLink }));
    };

    const onOkClick = async () => {
        await formValidation.handleSubmit();
    };

    return (
        <Modal show={true} onHide={onCancelClick} dialogClassName="base-modal" size="lg">
            <Form onSubmit={handleSubmit} noValidate>
                <ModalHeader closeButton className={classNames({ "modal-border-bottom": true })}>
                    <Modal.Title>
                        {props.data.isUpdate
                            ? `${t(StringResources.modal.createTokenizeLink.updateTitle)}`
                            : props.data.isCopy
                            ? `${t(StringResources.modal.createTokenizeLink.copyTitle)}`
                            : `${t(StringResources.modal.createTokenizeLink.title)}`}
                    </Modal.Title>
                </ModalHeader>
                <Modal.Body className="create-tokenize-link__modal-body">
                    {isLoading ? (
                        <div className="create-tokenize-link__modal-loader">
                            <OpacityLoader />
                        </div>
                    ) : (
                        <>
                            <div className="create-tokenize-link__settings-info">
                                <div className="create-tokenize-link__title">{`${t(
                                    StringResources.modal.createTokenizeLink.settingsTitle
                                )}`}</div>
                                <div className="create-tokenize-link__settings-columns">
                                    <div className="create-tokenize-link__settings-first-column">
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseSelect
                                                label={`${t(
                                                    StringResources.modal.createTokenizeLink.reasonForTokenization
                                                )} *`}
                                                options={reasonsForTokenization.map(x => {
                                                    return {
                                                        ...x,
                                                        selected:
                                                            x.value ==
                                                            formValidation.values["recurringProcessingModel"],
                                                    };
                                                })}
                                                onChange={option =>
                                                    formValidation.setFieldValue(
                                                        "recurringProcessingModel",
                                                        option.value
                                                    )
                                                }
                                                onBlur={e => {
                                                    formValidation.setTouched({ recurringProcessingModel: true });
                                                    formValidation.handleBlur(e);
                                                }}
                                                invalid={
                                                    formValidation.errors["recurringProcessingModel"] &&
                                                    formValidation.touched["recurringProcessingModel"]
                                                        ? formValidation.errors["recurringProcessingModel"]
                                                        : false
                                                }
                                                toolTipDefinitions={[
                                                    `${t(StringResources.modal.createTokenizeLink.cardOnFileTooltip)}`,
                                                    `${t(
                                                        StringResources.modal.createTokenizeLink
                                                            .unscheduledCardOnFileTooltip
                                                    )}`,
                                                    `${t(
                                                        StringResources.modal.createTokenizeLink.subscriptionTooltip
                                                    )}`,
                                                ]}
                                            />
                                        </div>
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseSelect
                                                label={`${t(
                                                    StringResources.modal.createTokenizeLink.tokenizeByLinkPageTheme
                                                )}`}
                                                options={ecomThemeOptions.map(x => {
                                                    return {
                                                        ...x,
                                                        selected: x.value == formValidation.values["themeId"],
                                                    };
                                                })}
                                                onChange={option =>
                                                    formValidation.setFieldValue("themeId", option?.value)
                                                }
                                                isClearable={true}
                                            />
                                        </div>
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseSelect
                                                label={`${t(
                                                    StringResources.modal.createTokenizeLink.languageOptionsLabel
                                                )} *`}
                                                options={languages.map(x => {
                                                    return {
                                                        label:
                                                            x.name == "Icelandic"
                                                                ? `${t(
                                                                      StringResources.enums.language[
                                                                          SupportedLanguageEnum.Icelandic
                                                                      ]
                                                                  )}`
                                                                : `${t(
                                                                      StringResources.enums.language[
                                                                          SupportedLanguageEnum.English
                                                                      ]
                                                                  )}`,
                                                        value: x.value,
                                                        selected: x.value == formValidation.values["language"],
                                                    };
                                                })}
                                                onChange={option =>
                                                    formValidation.setFieldValue("language", option.value)
                                                }
                                                onBlur={e => {
                                                    formValidation.setTouched({ language: true });
                                                    formValidation.handleBlur(e);
                                                }}
                                                invalid={
                                                    formValidation.errors["language"] &&
                                                    formValidation.touched["language"]
                                                        ? formValidation.errors["language"]
                                                        : false
                                                }
                                            />
                                        </div>
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseSelect
                                                label={`${t(
                                                    StringResources.modal.createTokenizeLink.hasExpirationDate
                                                )} *`}
                                                options={[
                                                    {
                                                        label: `${t(StringResources.modal.createTokenizeLink.yes)}`,
                                                        value: true,
                                                        selected: formValidation.values["hasExpirationDate"] === true,
                                                    },
                                                    {
                                                        label: `${t(StringResources.modal.createTokenizeLink.no)}`,
                                                        value: false,
                                                        selected: formValidation.values["hasExpirationDate"] === false,
                                                    },
                                                ]}
                                                onChange={option => {
                                                    formValidation.setFieldValue("hasExpirationDate", option.value);
                                                    if (option.value === false) {
                                                        formValidation.setFieldValue("dateTo", undefined);
                                                    }
                                                }}
                                                invalid={
                                                    formValidation.errors["hasExpirationDate"] &&
                                                    formValidation.touched["hasExpirationDate"]
                                                        ? formValidation.errors["hasExpirationDate"]
                                                        : false
                                                }
                                                onBlur={e => {
                                                    formValidation.setTouched({ hasExpirationDate: true });
                                                    formValidation.handleBlur(e);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="create-tokenize-link__settings-second-column">
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseSelect
                                                label={`${t(
                                                    StringResources.modal.createTokenizeLink.ecomTerminalToUse
                                                )} *`}
                                                options={ecomTerminals.map(x => {
                                                    return {
                                                        label: `${x.contractNumber} - ${x.dbaName} - ${x.currencyId} - ${x.tid}`,
                                                        value: `${x.contractId}-${x.ecomSolutionId}`,
                                                        selected:
                                                            `${x.contractId}-${x.ecomSolutionId}` ===
                                                            `${formValidation.values["contractId"]}-${formValidation.values["ecomSolutionId"]}`,
                                                    };
                                                })}
                                                onChange={option => onPaymentPageChange(option)}
                                                invalid={
                                                    formValidation.errors["ecomSolutionId"] &&
                                                    formValidation.touched["ecomSolutionId"]
                                                        ? formValidation.errors["ecomSolutionId"]
                                                        : false
                                                }
                                                onBlur={e => {
                                                    formValidation.setTouched({ ecomSolutionId: true });
                                                    formValidation.handleBlur(e);
                                                }}
                                            />
                                        </div>
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseInput
                                                type={"text"}
                                                name="tokenizeByLinkDescription"
                                                value={formValidation.values["tokenizeByLinkDescription"]}
                                                label={`${t(
                                                    StringResources.modal.createTokenizeLink.tokenizeByLinkDescription
                                                )}`}
                                                onBlur={formValidation.handleBlur}
                                                onChange={formValidation.handleChange}
                                            />
                                        </div>
                                        <div className="create-tokenize-link__settings-input">
                                            <BaseSelect
                                                label={`${t(StringResources.modal.createTokenizeLink.linkReUse)} *`}
                                                options={[
                                                    {
                                                        label: `${t(
                                                            StringResources.modal.createTokenizeLink
                                                                .tokenizeLinkReuseYes
                                                        )}`,
                                                        value: true,
                                                        selected: formValidation.values["linkReuse"] === true,
                                                    },
                                                    {
                                                        label: `${t(
                                                            StringResources.modal.createTokenizeLink.tokenizeLinkReuseNo
                                                        )}`,
                                                        value: false,
                                                        selected: formValidation.values["linkReuse"] === false,
                                                    },
                                                ]}
                                                onChange={option =>
                                                    formValidation.setFieldValue("linkReuse", option.value)
                                                }
                                            />
                                        </div>
                                        {formValidation.values["hasExpirationDate"] === true && (
                                            <div className="create-tokenize-link__settings-input">
                                                <BaseUtcDatePicker
                                                    label={`${t(StringResources.modal.createTokenizeLink.validTo)} *`}
                                                    onDateChange={e =>
                                                        formValidation.setFieldValue(
                                                            "dateTo",
                                                            e ? e.toISOString() : undefined
                                                        )
                                                    }
                                                    value={
                                                        formValidation.values["dateTo"]
                                                            ? new Date(formValidation.values["dateTo"])
                                                            : undefined
                                                    }
                                                    minDate={tomorrow.toJSDate()}
                                                    invalid={
                                                        formValidation.errors["dateTo"]
                                                            ? formValidation.errors["dateTo"]
                                                            : false
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <div className="create-tokenize-link__title">{`${t(
                                        StringResources.modal.createTokenizeLink.cardholderInfoTitle
                                    )}`}</div>
                                    {formValidation.values.shopperInfoData.map((item, index) => (
                                        <div key={item.field} className="create-tokenize-link__cardholder-info-options">
                                            <div className="create-tokenize-link__cardholder-info-options-label">
                                                {item.label}
                                            </div>

                                            <div className="create-tokenize-link__cardholder-info-options-option">
                                                <BaseToggleSwitch
                                                    options={[
                                                        {
                                                            label: t(
                                                                StringResources.modal.createTokenizeLink.hiddenLabel
                                                            ),
                                                            value: ShopperInfoRequirementType.Hidden,
                                                        },
                                                        {
                                                            label: t(
                                                                StringResources.modal.createTokenizeLink.optionalabel
                                                            ),
                                                            value: ShopperInfoRequirementType.Optional,
                                                        },
                                                        {
                                                            label: t(
                                                                StringResources.modal.createTokenizeLink.requiredLabel
                                                            ),
                                                            value: ShopperInfoRequirementType.Required,
                                                        },
                                                        {
                                                            label: t(
                                                                StringResources.modal.createTokenizeLink.prefilledLabel
                                                            ),
                                                            value: ShopperInfoRequirementType.Prefilled,
                                                        },
                                                    ]}
                                                    activeValue={item.shopperInfoRequirementType}
                                                    onChange={option =>
                                                        formValidation.setFieldValue(
                                                            `shopperInfoData[${index}].shopperInfoRequirementType`,
                                                            option.value
                                                        )
                                                    }
                                                />

                                                {item.shopperInfoRequirementType ===
                                                    ShopperInfoRequirementType.Prefilled && (
                                                    <div className="create-tokenize-link__cardholder-info-prefilled-input">
                                                        <BaseInput
                                                            type="text"
                                                            name={`shopperInfoData[${index}].value`}
                                                            label={formValidation.values.shopperInfoData[index].label}
                                                            value={formValidation.values.shopperInfoData[index].value}
                                                            onChange={formValidation.handleChange}
                                                            onBlur={() => {
                                                                formValidation.setFieldTouched(
                                                                    `shopperInfoData[${index}].value`,
                                                                    true
                                                                );
                                                                formValidation.validateField(
                                                                    `shopperInfoData[${index}].value`
                                                                );
                                                            }}
                                                            invalid={
                                                                formValidation.touched.shopperInfoData?.[index]
                                                                    ?.value &&
                                                                typeof formValidation.errors.shopperInfoData?.[
                                                                    index
                                                                ] === "object" &&
                                                                (
                                                                    formValidation.errors.shopperInfoData?.[
                                                                        index
                                                                    ] as FormikErrors<ITokenizeLinkShopperInfo>
                                                                )?.value
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className={classNames({ "modal-border-top": true })}>
                    <BaseButton
                        handleClick={onCancelClick}
                        text={`${t(StringResources.modal.cancel)}`}
                        styleType="line"
                    />

                    <BaseButton
                        handleClick={onOkClick}
                        text={props.actionButtonText}
                        styleType="solid"
                        disabled={!(formValidation.isValid && !formValidation.isValidating) && !isLoading}
                    />
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateTokenizeLinkModal;
